/**
 * Centralized Query Key Factory
 *
 * Provides type-safe, team-scoped query keys for all React Query caching.
 *
 * @pattern ['team', teamId, 'namespace', 'action', ...params]
 * @pattern ['no-team', 'namespace', ...] when teamId is null
 *
 * Usage:
 * ```typescript
 * const { user } = useAuth();
 * const teamId = getEffectiveTeamId(user);
 *
 * // Fetch posts for current team
 * useQuery({
 *   queryKey: teamQueryKeys.posts.list(teamId, { status: 'scheduled' }),
 *   queryFn: () => postsService.getAll(teamId, { status: 'scheduled' }),
 *   enabled: !!teamId
 * });
 *
 * // Invalidate all team data when switching teams
 * queryClient.invalidateQueries({ queryKey: teamQueryKeys.all(teamId) });
 * ```
 */

import { User } from '../types';
import { MediaLibraryFilters } from '../types/media-library';
import { CalendarPostsParams, SidebarFilters } from '../types/calendar';
import type { BoardFilters, ReleaseBoardColumnKey } from '../types/releaseBoard';
import type { ReleaseInboxSectionKey } from '../types/releaseSections';

/**
 * Post Filters
 */
export interface PostFilters {
  status?: 'draft' | 'scheduled' | 'published' | 'failed' | 'pending' | 'approved';
  platform?: string;
  calendarId?: number;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Channel Filters
 */
export interface ChannelFilters {
  platform?: string;
  isActive?: boolean;
}

/**
 * Dashboard Stats Filters
 */
export interface DashboardStatsFilters {
  dateFrom?: string;
  dateTo?: string;
}

/**
 * AI Usage Filters
 */
export interface AIUsageFilters {
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Notification Filters
 */
export interface NotificationFilters {
  type?: string;
  isRead?: boolean;
  page?: number;
  limit?: number;
}

/**
 * Portfolio-scoped keys. NOT team-scoped on purpose: the Brand Portfolio bell
 * spans every brand the user can access, so it must not change with a team id.
 */
export const portfolioNotificationKeys = {
  all: ['notifications-portfolio'] as const,
  list: (filters: { page?: number; limit?: number; isRead?: boolean; infinite?: boolean }) =>
    [...portfolioNotificationKeys.all, 'list', filters] as const,
  unreadCount: () => [...portfolioNotificationKeys.all, 'unreadCount'] as const,
};

/**
 * Brand Portfolio release inbox keys. Agency-scoped (NOT team-keyed), under ['agency', 'inbox'],
 * which invalidateReleaseState() already refreshes in every scope.
 */
export const releaseInboxKeys = {
  all: ['agency', 'inbox', 'release'] as const,
  section: (section: ReleaseInboxSectionKey, teamFilter: number | null) =>
    [...releaseInboxKeys.all, section, teamFilter ?? 'all'] as const,
  counts: () => [...releaseInboxKeys.all, 'counts'] as const,
};

/**
 * Helper function to get effective team ID
 * Returns currentTeamId (God Mode) or user's home teamId
 *
 * @param user - Current authenticated user
 * @returns Team ID or null if user has no team (e.g., agency staff without home team)
 */
export function getEffectiveTeamId(user: User | null): number | null {
  if (!user) return null;
  return user.currentTeamId || user.teamId || null;
}

/**
 * Team-scoped Query Keys Factory
 *
 * All keys follow the pattern: ['team', teamId, ...namespace]
 * When teamId is null, uses: ['no-team', ...namespace]
 *
 * This enables:
 * - Invalidating all data for a team: invalidateQueries(['team', teamId])
 * - Disabling queries when no team: enabled: !!teamId
 * - Switching teams: keys automatically change, data refetches
 */
export const teamQueryKeys = {
  /**
   * Root key for a team - invalidating this invalidates ALL team data
   */
  all: (teamId: number | null) =>
    teamId ? (['team', teamId] as const) : (['no-team'] as const),

  /**
   * Posts namespace
   */
  posts: {
    /**
     * All posts for a team
     */
    all: (teamId: number | null) =>
      [...teamQueryKeys.all(teamId), 'posts'] as const,

    /**
     * Filtered/paginated post list
     */
    list: (teamId: number | null, filters?: PostFilters) =>
      [...teamQueryKeys.posts.all(teamId), 'list', filters] as const,

    /**
     * Single post detail
     */
    detail: (teamId: number | null, postId: number) =>
      [...teamQueryKeys.posts.all(teamId), 'detail', postId] as const,

    /**
     * Post statistics (counts by status, platform, etc.)
     */
    stats: (teamId: number | null) =>
      [...teamQueryKeys.posts.all(teamId), 'stats'] as const,

    /**
     * Calendar view posts (month/week/day)
     */
    calendar: (teamId: number | null, params: CalendarPostsParams) =>
      [...teamQueryKeys.posts.all(teamId), 'calendar', params] as const,

    /**
     * Unscheduled/unmapped posts
     */
    unscheduled: (teamId: number | null, filters?: SidebarFilters) =>
      [...teamQueryKeys.posts.all(teamId), 'unscheduled', filters] as const,
  },

  /**
   * Channels namespace (connected social accounts)
   */
  channels: {
    /**
     * All channels for a team
     */
    all: (teamId: number | null) =>
      [...teamQueryKeys.all(teamId), 'channels'] as const,

    /**
     * Filtered channel list
     */
    list: (teamId: number | null, filters?: ChannelFilters) =>
      [...teamQueryKeys.channels.all(teamId), 'list', filters] as const,

    /**
     * Single channel detail
     */
    detail: (teamId: number | null, channelId: number) =>
      [...teamQueryKeys.channels.all(teamId), 'detail', channelId] as const,
  },

  /**
   * Calendars namespace (content calendars)
   */
  calendars: {
    /**
     * All calendars for a team
     */
    all: (teamId: number | null) =>
      [...teamQueryKeys.all(teamId), 'calendars'] as const,

    /**
     * Single calendar detail
     */
    detail: (teamId: number | null, calendarId: number) =>
      [...teamQueryKeys.calendars.all(teamId), 'detail', calendarId] as const,

    /**
     * Default calendar for team
     */
    default: (teamId: number | null) =>
      [...teamQueryKeys.calendars.all(teamId), 'default'] as const,
  },

  /**
   * Calendar Posts namespace (posts in calendar view)
   */
  calendarPosts: {
    /**
     * All calendar posts queries
     */
    all: (teamId: number | null) =>
      [...teamQueryKeys.all(teamId), 'calendarPosts'] as const,

    /**
     * Monthly calendar view
     */
    monthly: (teamId: number | null, year: number, month: number) =>
      [...teamQueryKeys.calendarPosts.all(teamId), 'monthly', year, month] as const,

    /**
     * Multi-calendar view
     */
    multiCalendar: (teamId: number | null, calendarIds: number[], params: CalendarPostsParams) =>
      [...teamQueryKeys.calendarPosts.all(teamId), 'multi', calendarIds, params] as const,
  },

  /**
   * Dashboard namespace
   */
  dashboard: {
    /**
     * All dashboard queries
     */
    all: (teamId: number | null) =>
      [...teamQueryKeys.all(teamId), 'dashboard'] as const,

    /**
     * Dashboard statistics
     */
    stats: (teamId: number | null, filters?: DashboardStatsFilters) =>
      [...teamQueryKeys.dashboard.all(teamId), 'stats', filters] as const,

    /**
     * Something That Worked (top performer)
     */
    somethingWorked: (teamId: number | null) =>
      [...teamQueryKeys.dashboard.all(teamId), 'somethingWorked'] as const,

    /**
     * Weekly Goals
     */
    goals: (teamId: number | null) =>
      [...teamQueryKeys.dashboard.all(teamId), 'goals'] as const,

    /**
     * Release sections (reviewer / operator rows). Under dashboard.all, so
     * invalidateReleaseState() already refreshes it.
     */
    releaseSections: (teamId: number | null, limit?: number) =>
      [...teamQueryKeys.dashboard.all(teamId), 'release-sections', limit] as const,
  },

  /**
   * Media Library namespace
   */
  mediaLibrary: {
    /**
     * All media library queries
     */
    all: (teamId: number | null) =>
      [...teamQueryKeys.all(teamId), 'mediaLibrary'] as const,

    /**
     * Paginated media list
     */
    list: (teamId: number | null, page: number, filters?: MediaLibraryFilters) =>
      [...teamQueryKeys.mediaLibrary.all(teamId), 'list', page, filters] as const,

    /**
     * Single media item
     */
    item: (teamId: number | null, mediaId: number) =>
      [...teamQueryKeys.mediaLibrary.all(teamId), 'item', mediaId] as const,

    /**
     * Storage statistics
     */
    stats: (teamId: number | null) =>
      [...teamQueryKeys.mediaLibrary.all(teamId), 'stats'] as const,
  },

  /**
   * AI namespace
   */
  ai: {
    /**
     * All AI queries
     */
    all: (teamId: number | null) =>
      [...teamQueryKeys.all(teamId), 'ai'] as const,

    /**
     * AI token usage
     */
    usage: (teamId: number | null, filters?: AIUsageFilters) =>
      [...teamQueryKeys.ai.all(teamId), 'usage', filters] as const,

    /**
     * AI brand profiles
     */
    brandProfiles: (teamId: number | null) =>
      [...teamQueryKeys.ai.all(teamId), 'brandProfiles'] as const,

    /**
     * Server-side draft for the "Create AI Brand Profile" wizard
     */
    brandProfileDraft: (teamId: number | null) =>
      [...teamQueryKeys.ai.all(teamId), 'brandProfileDraft'] as const,
  },

  /**
   * Package namespace (subscription/plan)
   */
  package: {
    /**
     * All package queries
     */
    all: (teamId: number | null) =>
      [...teamQueryKeys.all(teamId), 'package'] as const,

    /**
     * Team's current package
     */
    team: (teamId: number | null) =>
      [...teamQueryKeys.package.all(teamId), 'team'] as const,
  },

  /**
   * Approval Workflow namespace
   */
  approval: {
    /**
     * All approval queries
     */
    all: (teamId: number | null) =>
      [...teamQueryKeys.all(teamId), 'approval'] as const,

    /**
     * Team's approval workflow configuration
     */
    workflow: (teamId: number | null) =>
      [...teamQueryKeys.approval.all(teamId), 'workflow'] as const,

    /**
     * Approvals for a specific post
     */
    post: (teamId: number | null, postId: number) =>
      [...teamQueryKeys.approval.all(teamId), 'post', postId] as const,

    /**
     * Server-computed release permissions of the viewer for a post and its variations
     * (child of `post`, so invalidateReleaseState({ postId }) refreshes it).
     */
    releasePermissions: (teamId: number | null, postId: number) =>
      [...teamQueryKeys.approval.post(teamId, postId), 'release-permissions'] as const,

    /**
     * Brand-level release capabilities of the viewer (create mode in the composers).
     * Child of `all`, so a broad approval invalidation refreshes it.
     */
    releaseCapabilities: (teamId: number | null) =>
      [...teamQueryKeys.approval.all(teamId), 'release-capabilities'] as const,

    /**
     * Approval history for a post
     */
    history: (teamId: number | null, postId: number) =>
      [...teamQueryKeys.approval.all(teamId), 'history', postId] as const,

    /**
     * Comment thread of one version of a post (variation id, or "base" = Original).
     * Omit versionKey for the prefix covering every version of the post.
     */
    comments: (teamId: number | null, postId: number, versionKey?: number | 'base') =>
      versionKey === undefined
        ? ([...teamQueryKeys.approval.all(teamId), 'comments', postId] as const)
        : ([...teamQueryKeys.approval.all(teamId), 'comments', postId, versionKey] as const),

    /**
     * People that can be @mentioned in a post's chat
     */
    mentionable: (teamId: number | null, postId: number, internal: boolean) =>
      [...teamQueryKeys.approval.all(teamId), 'mentionable', postId, internal] as const,

    /**
     * Release Board drawer payload for a post (or one of its variations).
     * variationId null = base post.
     */
    releaseDetail: (teamId: number | null, postId: number, variationId: number | null) =>
      [
        ...teamQueryKeys.approval.all(teamId),
        'release-detail',
        postId,
        variationId ?? 'base',
      ] as const,
    reviewDiff: (teamId: number | null, postId: number, variationId: number | null) =>
      [...teamQueryKeys.approval.all(teamId), 'review-diff', postId, variationId ?? 'base'] as const,

    /**
     * Paged Release Board (one infinite query per column + counts).
     * invalidateReleaseState() invalidates the whole prefix.
     */
    board: {
      all: (teamId: number | null) =>
        [...teamQueryKeys.approval.all(teamId), 'board'] as const,

      facets: (teamId: number | null) =>
        [...teamQueryKeys.approval.board.all(teamId), 'facets'] as const,
      column: (teamId: number | null, column: ReleaseBoardColumnKey, filters: BoardFilters) =>
        [...teamQueryKeys.approval.board.all(teamId), 'column', column, filters] as const,

      counts: (teamId: number | null, filters: BoardFilters) =>
        [...teamQueryKeys.approval.board.all(teamId), 'counts', filters] as const,

      /** Nav badges (awaiting for you / newly released). Under board.all: invalidated with the board. */
      badges: (teamId: number | null) =>
        [...teamQueryKeys.approval.board.all(teamId), 'badges'] as const,

      /** Calendar nudge: items released since `since` (frozen per visit). Under board.all. */
      newlyReleased: (teamId: number | null, since: string | null) =>
        [...teamQueryKeys.approval.board.all(teamId), 'newly-released', since ?? 'initial'] as const,
    },
  },

  /**
   * Notifications namespace
   */
  notifications: {
    /**
     * All notification queries
     */
    all: (teamId: number | null) =>
      [...teamQueryKeys.all(teamId), 'notifications'] as const,

    /**
     * Paginated notification list
     */
    list: (teamId: number | null, filters?: NotificationFilters) =>
      [...teamQueryKeys.notifications.all(teamId), 'list', filters] as const,

    /**
     * Unread notification count
     */
    unreadCount: (teamId: number | null) =>
      [...teamQueryKeys.notifications.all(teamId), 'unreadCount'] as const,

    /**
     * User's notification preferences
     */
    preferences: (teamId: number | null) =>
      [...teamQueryKeys.notifications.all(teamId), 'preferences'] as const,
  },

  /**
   * Agency namespace (for agency staff viewing client workspaces)
   */
  agency: {
    /**
     * All agency queries
     */
    all: (teamId: number | null) =>
      [...teamQueryKeys.all(teamId), 'agency'] as const,

    /**
     * List of client workspaces (for agency staff)
     * NOTE: This uses agency's home teamId, not client teamId
     */
    workspaces: (agencyTeamId: number | null) =>
      [...teamQueryKeys.all(agencyTeamId), 'agency', 'workspaces'] as const,

    /**
     * Agency statistics
     * NOTE: This uses agency's home teamId
     */
    stats: (agencyTeamId: number | null) =>
      [...teamQueryKeys.all(agencyTeamId), 'agency', 'stats'] as const,

    /**
     * Workspace details when in God Mode
     */
    workspaceDetail: (teamId: number | null, workspaceId: number) =>
      [...teamQueryKeys.agency.all(teamId), 'workspace', workspaceId] as const,

    /**
     * Deep workspace search results (not team-scoped — spans entire agency)
     */
    search: (query: string) =>
      ['agency', 'workspaces', 'search', query] as const,
  },

  /**
   * Brand Check namespace (visual consistency checking)
   */
  brandCheck: {
    /**
     * All brand check queries
     */
    all: (teamId: number | null) =>
      [...teamQueryKeys.all(teamId), 'brandCheck'] as const,

    /**
     * Calendar View (posts grouped by calendar)
     */
    calendar: (teamId: number | null) =>
      [...teamQueryKeys.brandCheck.all(teamId), 'calendar'] as const,

    /**
     * Channel View (posts grouped by channel/platform)
     */
    channel: (teamId: number | null) =>
      [...teamQueryKeys.brandCheck.all(teamId), 'channel'] as const,
  },

  /**
   * Analytics namespace
   */
  analytics: {
    all: (teamId: number | null) =>
      [...teamQueryKeys.all(teamId), 'analytics'] as const,
    hub: (teamId: number | null, filters?: Record<string, unknown>) =>
      [...teamQueryKeys.analytics.all(teamId), 'hub', filters] as const,
    hubDetail: (teamId: number | null, channelInstanceId: number) =>
      [...teamQueryKeys.analytics.all(teamId), 'hubDetail', channelInstanceId] as const,
  },
};

/**
 * Usage Examples:
 *
 * 1. Fetch posts for current team:
 * ```typescript
 * const teamId = getEffectiveTeamId(user);
 * const { data } = useQuery({
 *   queryKey: teamQueryKeys.posts.list(teamId, { status: 'scheduled' }),
 *   queryFn: () => postsService.getAll(teamId, { status: 'scheduled' }),
 *   enabled: !!teamId
 * });
 * ```
 *
 * 2. Invalidate all posts after creating a new post:
 * ```typescript
 * queryClient.invalidateQueries({
 *   queryKey: teamQueryKeys.posts.all(teamId)
 * });
 * ```
 *
 * 3. Invalidate ALL team data when switching teams (God Mode):
 * ```typescript
 * queryClient.invalidateQueries({
 *   queryKey: teamQueryKeys.all(oldTeamId)
 * });
 * queryClient.invalidateQueries({
 *   queryKey: teamQueryKeys.all(newTeamId)
 * });
 * ```
 *
 * 4. Prefetch specific data:
 * ```typescript
 * await queryClient.prefetchQuery({
 *   queryKey: teamQueryKeys.posts.detail(teamId, postId),
 *   queryFn: () => postsService.getById(teamId, postId)
 * });
 * ```
 *
 * 5. Agency staff viewing client workspaces:
 * ```typescript
 * // List workspaces (uses agency's home teamId)
 * const agencyTeamId = user.teamId;
 * const { data } = useQuery({
 *   queryKey: teamQueryKeys.agency.workspaces(agencyTeamId),
 *   queryFn: () => agencyService.getWorkspaces()
 * });
 *
 * // After entering client workspace (God Mode):
 * const clientTeamId = user.currentTeamId; // Now points to client's team
 * const { data } = useQuery({
 *   queryKey: teamQueryKeys.posts.list(clientTeamId),
 *   queryFn: () => postsService.getAll(clientTeamId)
 * });
 * ```
 */
